import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Avatar } from '@companycam/slab-web';

import SelectCircle from './SelectCircle';

const Thumbnail = styled.div`
  position: relative;
  width: 100%;
  padding-top: 100%;
  background-size: cover;
  background-position: 50%;
  border-radius: var(--cc_size_border_radius_l);
  cursor: pointer;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  border-radius: var(--cc_size_border_radius_l);
  background: ${(props) =>
    props.selected
      ? 'var(--cc_color_background_backdrop_alpha_80)'
      : 'linear-gradient(transparent 50%, rgba(0,0,0,0.3))'};
  padding: ${(props) => (props.assetSize === 'small' ? '0' : '8px')};

  ${Thumbnail}:hover & {
    background: var(--cc_color_background_backdrop_alpha_90);
  }
`;

const Controls = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--cc_size_spacing_s);
  opacity: ${(props) => (props.selected ? 1 : 0)};

  ${Thumbnail}:hover & {
    opacity: 1;
  }
`;

const Video = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
`;

const PlayIcon = styled.i`
  opacity: 0.8;
  color: var(--cc_color_link_secondary);
  font-size: 40px;

  ${Thumbnail}:hover & {
    opacity: 1;
  }
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  opacity: 1;
  margin-top: auto;
  padding: 8px;
`;

const StatusIcons = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--cc_size_spacing_xs);
  color: white;
`;

/**
 * @deprecated Use { AssetThumbnail } from `@companycam/slab-web` instead
 *
 * https://slab-web.cocam.co/?path=/docs/assetthumbnail--docs
 */

const AssetThumbnail = ({
  creatorName,
  creatorURI,
  hasComments,
  hasDescription,
  hasTags,
  internal,
  src,
  isVideo,
  selected,
  toggleSelected,
  enableSelection,
  children,
  onClick,
  avatarColor,
  assetSize,
}) => {
  const container = useRef();

  const clickHandler = (e) => {
    if (container.current && container.current.contains(e.target)) {
      onClick(e);
    }
  };

  return (
    <Thumbnail
      ref={container}
      style={{ backgroundImage: `url(${src})` }}
      onClick={clickHandler}
      data-testid="assetfeed__asset-thumbnail"
    >
      <Overlay selected={selected} assetSize={assetSize}>
        <Controls
          selected={selected}
          data-testid="assetfeed__asset-thumbnail-controls"
        >
          <SelectCircle
            data-testid="assetfeed__asset-thumbnail-select-circle"
            onClick={(e) => {
              e.stopPropagation();
              toggleSelected(e);
            }}
            selected={selected}
            visible={enableSelection}
          />
          {children}
        </Controls>
        {isVideo ? (
          <Video className="dark-environment">
            <PlayIcon className="mdi mdi-play-circle-outline" />
          </Video>
        ) : null}
        <Meta>
          <Avatar
            size="small"
            imageSrc={creatorURI}
            fullName={creatorName}
            borderColor={avatarColor}
          />
          <StatusIcons>
            {internal ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                className="hint--bottom"
                aria-label="Hidden in Timeline"
              >
                <i className="mdi mdi-eye-off" />
              </div>
            ) : null}
            {hasDescription ? (
              <i
                className="mdi mdi-text"
                style={{ fontSize: 'var(--cc_size_text_m)' }}
              />
            ) : null}
            {hasComments ? <i className="mdi mdi-chat" /> : null}
            {hasTags ? (
              <i
                className="mdi mdi-tag"
                style={{ fontSize: 'var(--cc_size_text_s)' }}
              />
            ) : null}
          </StatusIcons>
        </Meta>
      </Overlay>
    </Thumbnail>
  );
};

AssetThumbnail.propTypes = {
  creatorName: PropTypes.string,
  creatorURI: PropTypes.string,
  hasComments: PropTypes.bool,
  hasDescription: PropTypes.bool,
  hasTags: PropTypes.bool,
  internal: PropTypes.bool,
  src: PropTypes.string,
  isVideo: PropTypes.bool,
  selected: PropTypes.bool,
  toggleSelected: PropTypes.func,
  enableSelection: PropTypes.bool,
  children: PropTypes.node,
  onClick: PropTypes.func,
  avatarColor: PropTypes.string,
  assetSize: PropTypes.string,
};

AssetThumbnail.defaultProps = {
  creatorName: '',
  creatorURI: '',
  hasComments: false,
  hasDescription: false,
  hasTags: false,
  src: '',
  isVideo: false,
  selected: false,
  toggleSelected: () => {},
  enableSelection: true,
  children: null,
  onClick: () => {},
  avatarColor: undefined,
  assetSize: undefined,
};

export default AssetThumbnail;
